"use client";

import React from 'react';
import Link from 'next/link';
import { ShaderBackground } from './ShaderBackground';
import { ArrowRight, Sparkles, Zap, Shield, Star, Check } from 'lucide-react';
import type { Tenant } from '@vm/theme';

interface LandingProps {
  tenant: Tenant;
}

export function Landing({ tenant }: LandingProps) {
  // Set CSS custom properties for gradient colors
  const gradientStyle = tenant.tokens.gradient ? {
    '--gradient-from': tenant.tokens.gradient.from,
    '--gradient-to': tenant.tokens.gradient.to,
  } as React.CSSProperties : {};

  const primaryRgb = hexToRgb(tenant.tokens.primary);
  const shaderColor: [number, number, number] = primaryRgb ? 
    [primaryRgb.r / 255, primaryRgb.g / 255, primaryRgb.b / 255] : 
    [0.8, 0.4, 1];

  return (
    <div className="flex flex-col min-h-screen bg-black text-white overflow-x-hidden" style={gradientStyle}>
      {/* Navigation */}
      <nav className="absolute top-0 w-full z-30 p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link href="/" className="flex items-center space-x-2 text-xl font-bold text-white" aria-label={`${tenant.name} Home`}>
            <Sparkles className="h-6 w-6" style={{ color: tenant.tokens.primary }} aria-hidden="true" />
            <span className="tenant-logo">{tenant.name}</span>
          </Link>
          <div className="hidden md:flex items-center space-x-6 text-white/80">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            {tenant.pricing && <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>}
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <Link href="/api/auth/signin" className="hover:text-white transition-colors">Sign In</Link>
          </div>
          <button className="md:hidden text-white hamburger mobile-menu-toggle" aria-label="Open mobile menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Shader Background */}
        <ShaderBackground
          color={shaderColor}
          speed={0.8}
          amplitude={0.15}
          className="absolute inset-0"
        />
        
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Hero Content */}
        <div className="relative z-20 flex items-center justify-center min-h-screen px-6">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            {tenant.copy.badge && (
              <div className="inline-flex items-center px-4 py-2 rounded-full glass mb-8 animate-fade-up">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
                <span className="text-sm text-white/90">{tenant.copy.badge}</span>
              </div>
            )}
            
            {/* Main Heading */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-6 leading-tight animate-fade-up" style={{ animationDelay: '100ms' }}>
              <span className="block gradient-text-dynamic pb-2 animate-gradient-shift">
                {tenant.copy.heroTitle}
              </span>
            </h1>
            
            {/* Subheading */}
            <p className="text-xl md:text-2xl text-white/80 mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: '200ms' }}>
              {tenant.copy.heroSubtitle}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 animate-fade-up" style={{ animationDelay: '300ms' }}>
              <Link href="/api/auth/signin">
                <button className="btn-gradient-dynamic px-8 py-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 hover-glow flex items-center">
                  {tenant.copy.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </Link>
              {tenant.copy.ctaSecondary && (
                <Link href="#about">
                  <button className="glass glass-hover px-8 py-4 text-white font-semibold rounded-lg border-white/20 hover-lift">
                    {tenant.copy.ctaSecondary}
                  </button>
                </Link>
              )}
            </div>
            
            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {tenant.copy.features.slice(0, 3).map((feature, index) => {
                const icons = [Sparkles, Zap, Shield];
                const Icon = icons[index % icons.length];
                const gradientColors = [
                  'from-gradient-violet to-gradient-purple',
                  'from-gradient-pink to-rose-500', 
                  'from-gradient-cyan to-blue-500'
                ];
                
                return (
                  <div key={index} className="glass glass-hover rounded-xl p-6 hover-lift animate-fade-up" style={{ animationDelay: `${400 + index * 100}ms` }}>
                    <div className={`w-12 h-12 bg-gradient-to-br ${gradientColors[index]} rounded-lg mb-4 flex items-center justify-center hover-scale animate-glow-pulse`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-2">{feature.split(':')[0] || feature}</h3>
                    <p className="text-white/70 text-sm">{feature.split(':')[1] || `Advanced ${feature.toLowerCase()} capabilities for your platform.`}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="w-full py-24 bg-darker relative">
        {/* Background gradients */}
        <div className="absolute top-40 left-20 w-96 h-96 opacity-20 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${tenant.tokens.primary}, transparent)` }} />
        <div className="absolute bottom-20 right-20 w-96 h-96 opacity-20 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${tenant.tokens.accent || tenant.tokens.primary}, transparent)` }} />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {tenant.copy.featuresTitle?.split(' ').slice(0, -2).join(' ') || 'Powerful'} <span className="gradient-text-dynamic">{tenant.copy.featuresTitle?.split(' ').slice(-2).join(' ') || 'Features'}</span>
            </h2>
            <p className="text-gray-300 text-xl max-w-2xl mx-auto">
              {tenant.copy.featuresSubtitle || 'Everything you need to build and scale your platform with cutting-edge technology.'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {tenant.copy.features.map((feature, index) => {
              const icons = [Sparkles, Zap, Shield, Star, Check, ArrowRight];
              const Icon = icons[index % icons.length];
              const gradientColors = [
                'from-gradient-violet to-gradient-purple',
                'from-gradient-pink to-rose-500',
                'from-gradient-cyan to-blue-500'
              ];
              
              return (
                <div key={index} className="card-dark hover-lift hover-glow animate-fade-up group" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className={`w-14 h-14 rounded-lg bg-gradient-to-br ${gradientColors[index % 3]} mb-4 flex items-center justify-center group-hover:scale-110 transition-transform animate-gradient-shift`}>
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.split(':')[0] || feature}</h3>
                  <p className="text-gray-400 leading-relaxed">
                    {feature.split(':')[1] || `Comprehensive ${feature.toLowerCase()} functionality built for scale and reliability.`}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {tenant.pricing && (
        <section id="pricing" className="w-full py-24 bg-black relative overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 opacity-10 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${tenant.tokens.accent || tenant.tokens.primary}, transparent)` }} />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 opacity-10 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${tenant.tokens.primary}, transparent)` }} />
          
          <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                {tenant.copy.pricingTitle?.split(' ').slice(0, -2).join(' ') || 'Choose Your'} <span className="gradient-text-dynamic">{tenant.copy.pricingTitle?.split(' ').slice(-2).join(' ') || 'Growth Plan'}</span>
              </h2>
              <p className="text-gray-300 text-xl max-w-2xl mx-auto">
                {tenant.copy.pricingSubtitle || 'Start free, then scale with our flexible pricing. No hidden fees, cancel anytime.'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {tenant.pricing.plans.map((plan, index) => (
                <div 
                  key={index} 
                  className={`card-dark transition-all group relative animate-fade-up ${
                    plan.popular 
                      ? 'border-2 scale-105 hover-glow' 
                      : 'border-white/10 hover:border-white/30'
                  }`}
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                    borderColor: plan.popular ? tenant.tokens.primary : undefined
                  }}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="btn-gradient-dynamic text-white text-xs font-bold px-3 py-1 rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-3xl font-bold">{plan.price}</span>
                      <span className="text-gray-400">{plan.period}</span>
                    </div>
                    <p className="text-gray-400 mb-6">{plan.description}</p>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-sm">
                          <div className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: tenant.tokens.primary }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link href={plan.buttonLink}>
                      <button className={`w-full ${plan.popular ? 'btn-gradient-dynamic' : 'btn-outline'} px-4 py-3 rounded-lg font-semibold transition-all duration-200`}>
                        {plan.buttonText}
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section id="about" className="w-full py-24 bg-darker relative">
        <div className="absolute top-20 right-20 w-96 h-96 opacity-20 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${tenant.tokens.accent || tenant.tokens.primary}, transparent)` }} />
        <div className="absolute bottom-40 left-20 w-96 h-96 opacity-20 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${tenant.tokens.primary}, transparent)` }} />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {tenant.copy.aboutTitle?.split(' ').slice(0, -2).join(' ') || 'Powered by'} <span className="gradient-text-dynamic">{tenant.copy.aboutTitle?.split(' ').slice(-2).join(' ') || 'Modern Tech'}</span>
            </h2>
            <p className="text-gray-300 text-xl max-w-2xl mx-auto">
              {tenant.copy.aboutSubtitle || 'Our platform integrates the most advanced technologies to provide you with the best development experience.'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="space-y-6 animate-fade-up">
              <div className="inline-flex items-center px-4 py-2 rounded-full glass mb-4">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse" />
                <span className="text-sm text-white/90">Technology Stack</span>
              </div>
              
              <h3 className="text-3xl font-bold mb-4">
                Enterprise-Grade Architecture
              </h3>
              
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Built with modern technologies and best practices to ensure scalability, security, and performance for your growing business.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gradient-to-r rounded-full mr-4" style={{ 
                    background: `linear-gradient(to right, ${tenant.tokens.primary}, ${tenant.tokens.accent || tenant.tokens.primary})` 
                  }} />
                  <span className="text-gray-300">Next.js & TypeScript for type-safe development</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gradient-to-r rounded-full mr-4" style={{ 
                    background: `linear-gradient(to right, ${tenant.tokens.primary}, ${tenant.tokens.accent || tenant.tokens.primary})` 
                  }} />
                  <span className="text-gray-300">Supabase for real-time database and authentication</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gradient-to-r rounded-full mr-4" style={{ 
                    background: `linear-gradient(to right, ${tenant.tokens.primary}, ${tenant.tokens.accent || tenant.tokens.primary})` 
                  }} />
                  <span className="text-gray-300">Stripe for secure payment processing</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-8 animate-fade-up" style={{ animationDelay: '200ms' }}>
              <div className="card-dark hover-lift">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ 
                    background: `linear-gradient(to br, ${tenant.tokens.primary}, ${tenant.tokens.accent || tenant.tokens.primary})` 
                  }}>
                    <span className="text-white font-bold text-lg">∞</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Scalable Infrastructure</h4>
                    <p className="text-gray-400 text-sm">Built to handle millions of users</p>
                  </div>
                </div>
              </div>
              
              <div className="card-dark hover-lift">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ 
                    background: `linear-gradient(to br, ${tenant.tokens.primary}, ${tenant.tokens.accent || tenant.tokens.primary})` 
                  }}>
                    <span className="text-white font-bold text-lg">🛡️</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Enterprise Security</h4>
                    <p className="text-gray-400 text-sm">Bank-level encryption and protection</p>
                  </div>
                </div>
              </div>
              
              <div className="card-dark hover-lift">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ 
                    background: `linear-gradient(to br, ${tenant.tokens.primary}, ${tenant.tokens.accent || tenant.tokens.primary})` 
                  }}>
                    <span className="text-white font-bold text-lg">⚡</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Lightning Fast</h4>
                    <p className="text-gray-400 text-sm">Optimized for speed and performance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ 
          background: `linear-gradient(to right, ${tenant.tokens.primary}, transparent, ${tenant.tokens.accent || tenant.tokens.primary})` 
        }} />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="flex flex-col items-center space-y-4 text-center">
            <div className="space-y-2">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-center">
                {tenant.copy.ctaFinalTitle?.split(' ').slice(0, -2).join(' ') || 'Ready to'} <span className="gradient-text-dynamic">{tenant.copy.ctaFinalTitle?.split(' ').slice(-2).join(' ') || '10x Your Development'}</span>?
              </h2>
              <p className="mx-auto max-w-[600px] text-gray-300 text-xl">
                {tenant.copy.ctaFinalSubtitle || `Join thousands of developers using ${tenant.name} to build amazing products faster.`}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link href="/api/auth/signin">
                <button className="btn-gradient-dynamic px-8 py-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center">
                  {tenant.copy.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </Link>
              {tenant.pricing && (
                <button className="text-white hover:opacity-80 px-8 py-4 transition-opacity duration-200">
                  <a href="#pricing">View Pricing</a>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-darker">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-white/60 text-center md:text-left">
              <p>© 2025 {tenant.name}. All rights reserved.</p>
              <p className="mt-2">
                {tenant.name}™ is a product of <a href="https://vanmoose.com" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors">Van Moose</a>
              </p>
              <p className="mt-1">
                VAT: NL005268366B28
              </p>
              <p>Amsterdam, The Netherlands</p>
            </div>
            <nav className="flex gap-6">
              <Link className="text-sm text-white/60 hover:text-white transition-colors" href="/terms">
                Terms
              </Link>
              <Link className="text-sm text-white/60 hover:text-white transition-colors" href="/privacy">
                Privacy
              </Link>
              <Link className="text-sm text-white/60 hover:text-white transition-colors" href="/refund">
                Refund
              </Link>
              <Link className="text-sm text-white/60 hover:text-white transition-colors" href="/contact">
                Contact
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Utility function to convert hex to RGB
function hexToRgb(hex: string): {r: number, g: number, b: number} | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}